import CountdownWithDeliveryTypeCategory from '@/features/orders/components/CountdownWithDeliveryTypeCategory'
import DeliveryTypeCategoryAndChannel from '@/features/orders/components/DeliveryTypeCategoryAndChannel'
import OrderCompletionIndicator from '@/features/orders/components/OrderCompletionIndicator'
import { Phase } from '@/features/orders/constants/app'
import IconButton from '@/features/ui/buttons/components/IconButton'
import Badge from '@/features/ui/components/Badge'
import CloseIcon from '@/features/ui/icons/components/CloseIcon'
import { useAppDispatch, useAppSelector } from '@/hooks/redux'
import { useScreenLayout } from '@/hooks/screen'
import { clearSelectedOrderUuid } from '@/redux/reducers/orders'
import { ordersSelectors } from '@/redux/reducers/orders/selectors'
import { FC } from 'react'
import { StyleProp, StyleSheet, View, ViewStyle } from 'react-native'
import invariant from 'tiny-invariant'

interface ShortCodeAndDeliveryTypeCategoryAndChannelWithCountdownOrCompletionIndicatorProps {
    uuid: string
    style?: StyleProp<ViewStyle>
}

const ShortCodeAndDeliveryTypeCategoryAndChannelWithCountdownOrCompletionIndicator: FC<
    ShortCodeAndDeliveryTypeCategoryAndChannelWithCountdownOrCompletionIndicatorProps
> = ({ uuid, style }) => {
    const order = useAppSelector(ordersSelectors.orderByUuid(uuid))

    const dispatch = useAppDispatch()

    const screenLayout = useScreenLayout()

    invariant(order, 'Order must be set')

    const renderCountdownOrCompletionIndicator = () => {
        if (order.phase !== Phase.Archived) {
            const deliveryTime = order.acceptedDeliveryTime ?? order.requestedDeliveryTime

            invariant(deliveryTime, 'Order delivery time must be provided')

            return <CountdownWithDeliveryTypeCategory deliveryTime={deliveryTime} />
        }

        return (
            <OrderCompletionIndicator
                state={order.state}
                acceptedDeliveryTime={order.acceptedDeliveryTime}
                deliveredAt={order.deliveredAt}
            />
        )
    }

    const handleCloseButtonPress = () => {
        dispatch(clearSelectedOrderUuid())
    }

    return (
        <View style={[styles.container, style]}>
            <View style={styles.heading}>
                {order.shortCode && (
                    <Badge size="small" style={styles.shortCode}>
                        {order.shortCode}
                    </Badge>
                )}
                <DeliveryTypeCategoryAndChannel
                    deliveryTypeCategory={order.deliveryType.typeCategory}
                    channel={order.channel}
                />
            </View>
            {renderCountdownOrCompletionIndicator()}
            {screenLayout === 'extended' && (
                <IconButton
                    Icon={CloseIcon}
                    onPress={handleCloseButtonPress}
                    size={24}
                    iconSize={24}
                />
            )}
        </View>
    )
}

const styles = StyleSheet.create({
    container: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 24,
    },
    heading: {
        flex: 1,
        minWidth: 0,
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
    },
    shortCode: {
        height: 22,
    },
})

export default ShortCodeAndDeliveryTypeCategoryAndChannelWithCountdownOrCompletionIndicator
